import type { BrowserOptions, ErrorEvent, EventHint } from '@sentry/react'
import axios from 'axios'

import { scrubSentryEvent } from '@beaded/shared-lib/src/redactSecrets.js'

import { isStaleChunkError } from '../staleChunkReload'

// HTTP status carried by errors thrown from api/index.ts handleError (or the
// raw AxiosError when re-thrown). Used to distinguish an expected 404 from a
// raw err.message leaked by a 500.
export function httpStatusOf(error: unknown): number | undefined {
  if (axios.isAxiosError(error)) return error.response?.status
  if (error instanceof Error && 'httpStatus' in error) {
    const status: unknown = error.httpStatus
    if (typeof status === 'number') return status
  }
  return undefined
}

/**
 * Sentry beforeSend filter -- drops events for known benign errors
 * that are not actionable (navigation aborts, network errors, stale chunks).
 */
export function sentryBeforeSend(
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null {
  const error = hint.originalException

  // Aborted fetch() throws DOMException with name 'AbortError'
  if (error instanceof DOMException && error.name === 'AbortError') return null

  const message =
    typeof error === 'string'
      ? error
      : error instanceof Error
        ? error.message
        : ''
  if (message.includes('not logged in')) return null
  if (message === 'Network Error') return null
  if (message === 'Request aborted') return null
  if (message === 'canceled') return null
  // Delegates to isStaleChunkError so this filter can't drift out of sync
  // with the router's own stale-chunk detection (all 4 browser-specific
  // dynamic-import-failure message variants: Chrome/MIME, Chrome/fetch,
  // Safari, Firefox).
  if (isStaleChunkError(error)) return null
  // Highcharts (third-party) fires mouseover/mouseout tracker events after a
  // chart has already been destroyed -- e.g. a SparkLine unmounts while the
  // cursor is still over it -- and reads chart.hoverSeries off the gone chart.
  // The race is internal to the library and benign (the user sees nothing).
  // `hoverSeries` is a Highcharts-internal property name, specific enough to key
  // on directly -- unlike the generic "Cannot read properties of undefined". We
  // deliberately do NOT also gate on a 'highcharts' stack frame: beforeSend runs
  // client-side on the raw (unsymbolicated) stack, whose frames are hashed bundle
  // URLs, so 'highcharts' would not appear and the filter would never fire in
  // production. (WEB-A0 / WEB-9Z)
  if (message.includes('hoverSeries')) return null
  // Resource-lookup 404s ("cable not found", "logger not found", etc.) are
  // expected when a user opens a deleted/invalid id from a stale link. The UI
  // already surfaces them via ErrorCard, so they're not actionable. Suppress
  // only when the response was an actual 404 AND the message is a bare
  // "<resource words> not found": a structured/prefixed 500 (e.g. "Error
  // swapping cables: cable not found") or a bare "<resource> not found" that
  // leaks from a non-404 (the API returns raw err.message on 500s too) still
  // reports. (WEB-8Z)
  if (
    httpStatusOf(error) === 404 &&
    /^[a-z ]+ not found$/.test(message.toLowerCase().trim())
  )
    return null
  // Google Maps' loader fails to fetch one of its own internal modules
  // ("onion") on a transient network hiccup. The throw originates inside
  // Google's minified code, so the app cannot catch it. (WEB-A4)
  if (message.includes('Could not load "onion"')) return null
  // posthog-js lazy-loads its session-recording recorder as an external
  // <script>. When an ad/content blocker or network intermediary serves that
  // asset with a 200 but an empty/neutered body, the script's 'load' event
  // fires yet __PosthogExtensions__.initSessionRecording is never registered,
  // so posthog throws this from inside the load handler. The throw is async
  // (post-remote-config, inside posthog internals), so neither initPostHog's
  // try/catch nor PostHogProvider's guards can catch it; it reaches
  // window.onerror. Recording just fails to start -- benign, non-actionable.
  // The message is unique to posthog-js session recording. (WEB-B7)
  if (
    message.includes(
      'Called on script loaded before session recording is available',
    )
  )
    return null
  // Reset / magic-link page URLs carry a live one-time token, and the SDK
  // copies the page URL, Referer and breadcrumbs into the event.
  return scrubSentryEvent(event)
}

// @sentry/react does not re-export TransactionEvent.
type TransactionEvent = Parameters<
  NonNullable<BrowserOptions['beforeSendTransaction']>
>[0]

/** Sentry beforeSendTransaction: the same URL / token scrubbing. */
export function sentryBeforeSendTransaction(
  event: TransactionEvent,
): TransactionEvent {
  return scrubSentryEvent(event)
}
